import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDownIcon, DownloadIcon, FileTextIcon, HistoryIcon, ImageIcon, MapPinIcon, PaperclipIcon, PlusIcon, SparklesIcon } from 'lucide-react';
import type { CitizenRequest, RequestAttachment } from '../../data/demandes';
const attachmentIcons: Record<RequestAttachment['type'], typeof ImageIcon> = {
  image: ImageIcon,
  pdf: FileTextIcon,
  form: FileTextIcon
};
export function RequestDetailsPanel({
  request
}: {
  request: CitizenRequest;
}) {
  const [historyOpen, setHistoryOpen] = useState(false);
  return <div className="space-y-5" data-id="element-910" data-mp="2jicr2es2c6b5">
      <section aria-labelledby="description-title" className="rounded-3xl bg-ville-sand p-6 sm:p-7" data-id="element-911" data-mp="2ji4b2e0n8kvq">
        <h2 id="description-title" className="text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-912" data-mp="2oge9qylku3m3">
          Request description
        </h2>
        <p className="mt-3 text-[16px] leading-relaxed text-slate-700" data-id="element-913" data-mp="p6ft02z48274">{request.description}</p>
        <p className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-slate-500" data-id="element-914" data-mp="u5irf4klba64">
          <SparklesIcon className="h-3.5 w-3.5 text-ville-indigo" aria-hidden="true" data-id="element-915" data-mp="2k3lv7ywiz4xd" />
          Request created by {request.createdBy}
        </p>
      </section>

      <div className="grid gap-5 sm:grid-cols-2" data-id="element-916" data-mp="2bb21ncv3bqje">
        <section aria-labelledby="location-title" className="rounded-3xl border border-slate-200 p-6" data-id="element-917" data-mp="2b7minyyapxcp">
          <h2 id="location-title" className="text-sm font-extrabold uppercase tracking-wider text-slate-500" data-id="element-918" data-mp="3316hqzbthret">
            Location
          </h2>
          <p className="mt-3 flex items-start gap-2 text-[15px] font-bold leading-snug text-ville-navy" data-id="element-919" data-mp="2z0mmk1at0xvb">
            <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" data-id="element-920" data-mp="2tz6xzz9f4986" />
            {request.address}
          </p>
          <p className="mt-1 pl-6 text-sm text-slate-500" data-id="element-921" data-mp="2p3rmhdyvupex">{request.fullAddress}</p>
        </section>

        <section aria-labelledby="dates-title" className="rounded-3xl border border-slate-200 p-6" data-id="element-922" data-mp="1iiyvfxtzkxwi">
          <h2 id="dates-title" className="text-sm font-extrabold uppercase tracking-wider text-slate-500" data-id="element-923" data-mp="1dol2s36pl4vj">
            Dates
          </h2>
          <dl className="mt-3 space-y-2 text-[15px]" data-id="element-924" data-mp="1k2qn3qvho049">
            <div className="flex items-baseline justify-between gap-4" data-id="element-925" data-mp="1k04hvxw2lwp7">
              <dt className="text-slate-500" data-id="element-926" data-mp="1627nn853a2dp">Created</dt>
              <dd className="font-bold tabular-nums text-ville-navy" data-id="element-927" data-mp="1b1ao0fa4jkfc">{request.createdOn}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-4" data-id="element-928" data-mp="wco3lz5n42h9">
              <dt className="text-slate-500" data-id="element-929" data-mp="113crz5m0wjc6">Resolved</dt>
              <dd className={`font-bold tabular-nums ${request.resolvedOn ? 'text-ville-navy' : 'text-slate-300'}`} data-id="element-930" data-mp="114xmiv8c82a1">
                {request.resolvedOn ?? '-- / -- / --'}
              </dd>
            </div>
          </dl>
        </section>
      </div>

      <section aria-labelledby="files-title" className="rounded-3xl border border-slate-200 p-6 sm:p-7" data-id="element-931" data-mp="ddigoshcr2u1">
        <h2 id="files-title" className="text-sm font-extrabold uppercase tracking-wider text-slate-500" data-id="element-932" data-mp="idekaqm50s92">
          Attached files
        </h2>

        {request.attachments.length === 0 ? <p className="mt-3 text-[15px] text-slate-500" data-id="element-933" data-mp="3pxw8rj6uoxye">No attached files.</p> : <ul className="mt-4 space-y-2" data-id="element-934" data-mp="3pzqwt6df566p">
            {request.attachments.map(file => {
          const Icon = attachmentIcons[file.type];
          return <li key={file.name} className="flex items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3" data-id="element-935" data-mp="3q6g49r8xn6l">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-ville-mist text-ville-deep" aria-hidden="true" data-id="element-936" data-mp="howdlqhz9ovh">
                    <Icon className="h-5 w-5" data-id="element-937" data-mp="cp9ytcmi40kk" />
                  </span>
                  <span className="min-w-0 flex-1" data-id="element-938" data-mp="rfhb1alx0nct">
                    <span className="block truncate text-[15px] font-bold text-ville-navy" data-id="element-939" data-mp="mge23f2tdwzk">{file.name}</span>
                    <span className="block text-xs text-slate-500" data-id="element-940" data-mp="10fx9u6rpr6bc">{file.size}</span>
                  </span>
                  <button type="button" aria-label={`Download ${file.name}`} className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-941" data-mp="wg4xrk342i18">
                    <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-942" data-mp="2zoyn5e29w7jb" />
                  </button>
                </li>;
        })}
          </ul>}

        <button type="button" className="mt-4 inline-flex items-center gap-2 rounded-2xl border border-dashed border-slate-300 px-4 py-3 text-sm font-bold text-slate-500 transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-943" data-mp="26yyd70sx049l">
          <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-944" data-mp="2l0ap914yflq7" />
          Add a file
          <PaperclipIcon className="h-4 w-4 opacity-50" aria-hidden="true" data-id="element-945" data-mp="3sbja82bptxh2" />
        </button>
      </section>

      <section aria-labelledby="history-title" className="rounded-3xl border border-slate-200 px-6 py-4 sm:px-7" data-id="element-946" data-mp="6xj7vma3ply">
        <h2 id="history-title" className="sr-only" data-id="element-947" data-mp="1kprj51loty03">
          Request history
        </h2>
        <button type="button" onClick={() => setHistoryOpen(open => !open)} aria-expanded={historyOpen} className="flex w-full items-center justify-between gap-4 py-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-948" data-mp="1gphdjq1t59lr">
          <span className="inline-flex items-center gap-2 rounded-full bg-ville-mist px-3 py-1.5 text-sm font-bold text-ville-deep" data-id="element-949" data-mp="120kz9b95r0cp">
            <HistoryIcon className="h-4 w-4" aria-hidden="true" data-id="element-950" data-mp="x072wqgx5eui" />
            History
          </span>
          <span className="flex items-center gap-2 text-sm text-slate-500" data-id="element-951" data-mp="szww5blaykel">
            {request.timeline.length} steps
            <ChevronDownIcon className={`h-5 w-5 transition-transform duration-200 ease-ville ${historyOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-952" data-mp="o2d7dhqzap8e" />
          </span>
        </button>

        {historyOpen && <motion.ol initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="mt-4 space-y-4 border-t border-slate-100 pt-4" data-id="element-953" data-mp="2ic8ew2erw3jq">
            {request.timeline.map((step, index) => <li key={step.label} className="flex gap-4" data-id="element-954" data-mp="2sgf373rk75fq" data-mpi={index}>
                <div className="flex flex-col items-center" data-id="element-955" data-mp="2xi4g6oycdt6g">
                  <span className={`h-3 w-3 rounded-full ${index === request.timeline.length - 1 ? 'bg-ville-indigo' : 'bg-slate-300'}`} aria-hidden="true" data-id="element-956" data-mp="3l76x1ul5jhzw" />
                  {index < request.timeline.length - 1 && <span className="mt-1 w-px flex-1 bg-slate-200" aria-hidden="true" data-id="element-957" data-mp="3b8zs6a450qe9" />}
                </div>
                <div className="pb-1" data-id="element-958" data-mp="3g7b20kufn0wh">
                  <p className="text-sm font-bold text-ville-navy" data-id="element-959" data-mp="3g69gfmfsiw0i">{step.label}</p>
                  <p className="text-sm text-slate-500" data-id="element-960" data-mp="1zsjd4hwhwbrx">{step.date}</p>
                </div>
              </li>)}
          </motion.ol>}
      </section>
    </div>;
}